import { IEntityFormField, INPUT_TYPE } from '../components/shared/forms/entityForm.interface';
import {
  IonContent,
  IonDatetime,
  IonDatetimeButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPopover,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToggle,
} from '@ionic/react';
import * as React from 'react';
import { useRef, useState } from 'react';
import { TextFieldTypes } from '@ionic/core';
import i18next from 'i18next';
import { SORT_BY_EXPENSES, SORT_DIRECTION } from '../interfaces/enumerables';
import { SelectItemCard } from '../components/shared/ItemCard/SelectItemCard';
import { GeotrianLoading } from '../components/shared/loading/GeotrianLoading';
import { closeCircleOutline } from 'ionicons/icons';
import moment from 'moment';
import { getISOStringWithRoundSeconds, getLocalDate, isDesktop } from './helpers';
import { MultiSelectSearch } from '../components/shared/MultiSelectSearch';

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const isDateField = (fieldName: string) => {
  return ['pubDate', 'deadline', 'doneDate'].includes(fieldName);
};

const getUpdatedDateField = (
  initialData: Record<string, any>,
  submitData: Record<string, any>,
  fieldName: string,
): any | undefined => {
  let initialField = initialData[fieldName];
  let submitField = submitData[fieldName];

  if (isDateField(fieldName)) {
    initialField = getISOStringWithRoundSeconds(initialField);
    submitField = getISOStringWithRoundSeconds(submitField);
  }

  if (initialField !== submitField) {
    return submitField;
  }

  // No change in the field
  return undefined;
};

export const getChangedFields = (
  arrayOfFields: IEntityFormField[],
  initialData: Record<string, any>,
  submitData: Record<string, any>,
) => {
  const updatedData: Record<string, any> = {};

  arrayOfFields.forEach((field) => {
    const submitField = getUpdatedDateField(initialData, submitData, field.name);
    if (submitField !== undefined) updatedData[field.name] = submitField;
  });

  return updatedData;
};

export const formDataToSubmit = (fields: IEntityFormField[], dataToSubmit: Record<string, any>) => {
  fields.forEach((field) => {
    // Select specific field to be sent to the server
    if (field.fieldToSubmit) {
      if (dataToSubmit?.[field.name]) {
        dataToSubmit[field.name] = dataToSubmit?.[field.name]?.[field.fieldToSubmit];
      }
    }

    // Use function map specific to the field to map form-data to be sent to the server
    if (field.fieldMap) {
      if (dataToSubmit?.[field.name]) {
        dataToSubmit[field.name] = field.fieldMap(dataToSubmit[field.name]);
      }
    }

    // Replace comma from decimal number with valid dot annotation
    if (['value', 'payed'].includes(field.name) && dataToSubmit?.[field.name]) {
      dataToSubmit[field.name] = String(dataToSubmit?.[field.name])?.replace(',', '.');
    }
  });

  return dataToSubmit;
};

export const mapToFormInput: Record<INPUT_TYPE, any> = {
  [INPUT_TYPE.MULTI_SELECT_SEARCH]: {
    component: (field: IEntityFormField, formik: any) => <MultiSelectSearch field={field} formik={formik} />,
  },
  [INPUT_TYPE.SELECT_SEARCH]: {
    component: (field: IEntityFormField, formik: any) => {
      const modal = useRef<HTMLIonModalElement>(null);
      const [search, setSearch] = useState<string>('');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { isLoading, data, isError, error } = field.useHook(1, SORT_BY_EXPENSES.ID, SORT_DIRECTION.ASC, search);

      const renderList = () => {
        if (data && data?.data?.length > 0) {
          return data.data.map((item: any) => {
            return (
              <SelectItemCard
                key={item?.id}
                id={item?.id}
                field={item?.[field.fieldValue || 'id']}
                field2={item?.[field.fieldValue2 || '']}
                onClick={() => {
                  formik.setFieldValue(field?.name, item);
                  modal.current?.dismiss();
                }}
              />
            );
          });
        }
      };

      const handleSearchChange = (ev: any) => {
        setSearch(ev?.detail?.value || '');
      };

      const clearInput = (e: any) => {
        e.stopPropagation();
        formik.setFieldValue(field?.name, null);
      };

      const isValueSelected = () => {
        {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
        {/*@ts-ignore*/}
        if (formik?.values?.[field?.name]?.[field?.fieldValue] || formik?.values?.[field?.name]?.[field?.fieldValue2]) {
          return true;
        }
        return false;
      };

      const getPicker = () => {
        if (isDesktop()) {
          return (
            <IonPopover trigger={`open-picker-${field.name}`} triggerAction="click" size="cover">
              <IonContent className="ion-padding">
                <IonSearchbar
                  value={search}
                  debounce={200}
                  placeholder={i18next.t('FILTERS.SEARCH')}
                  onIonChange={handleSearchChange}
                  onClick={() => modal.current?.setCurrentBreakpoint(0.75)}
                />
                <div className="list-items-itemOfList padding-b-30 list-padding-horizontal">{renderList()}</div>
                {isLoading && <GeotrianLoading />}
              </IonContent>
            </IonPopover>
          );
        }

        return (
          <IonModal
            ref={modal}
            trigger={`open-picker-${field?.name}`}
            initialBreakpoint={0.25}
            breakpoints={[0, 0.25, 0.5, 0.75]}>
            <IonContent className="ion-padding">
              <IonSearchbar
                value={search}
                debounce={200}
                placeholder={i18next.t('FILTERS.SEARCH')}
                onIonChange={handleSearchChange}
                onClick={() => modal.current?.setCurrentBreakpoint(0.75)}
              />
              <div className="list-items-itemOfList padding-b-30 list-padding-horizontal">{renderList()}</div>
              {isLoading && <GeotrianLoading />}
            </IonContent>
          </IonModal>
        );
      };

      return (
        <div>
          <div className="g-select-clear">
            <IonIcon icon={closeCircleOutline} style={{ fontSize: 24 }} onClick={clearInput}></IonIcon>
          </div>
          <IonItem
            mode="md"
            className="g-select-size border-radius-input g-select-border cursorPointer flex-center-center-row-wrap"
            id={`open-picker-${field.name}`}>
            <div className="flex-start-center-column-nowrap g-select width-100 height-100 border-radius-24 width-250px">
              <div className="sc-ion-label-md-s normalText3">{i18next.t(`FORMS.${field?.label}`)}</div>
              {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
              {/*@ts-ignore*/}
              {isValueSelected() && (
                <div>
                  {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
                  {/*@ts-ignore*/}
                  {formik?.values?.[field?.name]?.[field?.fieldValue]}{' '}
                  {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
                  {/*@ts-ignore*/}
                  {formik?.values?.[field?.name]?.[field?.fieldValue2]}
                </div>
              )}
            </div>
          </IonItem>
          {getPicker()}
        </div>
      );
    },
  },
  [INPUT_TYPE.SELECT]: {
    component: (field: IEntityFormField, formik: any) => {
      const formValue = formik.values?.[field.name];

      const onChange = (ev: any) => {
        formik.setFieldValue(field.name, ev?.detail?.value);
      };

      const getOptions = () => {
        return field?.data?.map((option, index) => {
          return (
            <IonSelectOption key={index} value={option?.value}>
              {i18next.t(option?.label)}
            </IonSelectOption>
          );
        });
      };

      return (
        <IonItem className="width-300px border-radius-input g-select-border flex-center-center-column-nowrap" mode="md">
          <div className="sc-ion-label-md-s normalText3 g-static-select-label">
            {i18next.t(`FORMS.${field?.label}`)}
          </div>
          <IonSelect
            disabled={field.disabled}
            className="border-radius-24 simple-select g-static-select"
            name={field.name}
            mode="ios"
            value={formValue}
            interface={isDesktop() ? 'popover' : 'action-sheet'}
            onIonChange={onChange}
            placeholder={i18next.t('NOTIFICATIONS.NOTIFICATION_TYPE')}
            cancelText={i18next.t('FORMS.CANCEL')}>
            {getOptions()}
          </IonSelect>
        </IonItem>
      );
    },
  },
  [INPUT_TYPE.TOGGLE]: {
    component: (field: IEntityFormField, formik: any) => {
      const formValue = formik.values?.[field.name];

      return (
        <IonItem className="width-300px border-radius-input g-select-border" mode="md">
          <div className="g-toggle-container border-radius-24">
            <div className="sc-ion-label-md-s normalText4 border-radius-24">{i18next.t(`FORMS.${field.label}`)}</div>
            <IonToggle
              disabled={field.disabled}
              name={field.name}
              mode="ios"
              checked={!!formValue}
              className="border-radius-24"
              value={formValue}
              onClick={() => formik.setFieldValue(field.name, !formValue)}></IonToggle>
          </div>
        </IonItem>
      );
    },
  },
  [INPUT_TYPE.TEXT_AREA]: {
    component: (field: IEntityFormField, formik: any) => {
      const formValue = formik.values?.[field.name];

      const onChange = (ev: any) => {
        formik.setFieldValue(field.name, ev?.detail?.value);
      };

      return (
        <IonItem className="width-300px border-radius-input g-select-border" mode="md">
          <div className="g-text-area-container border-radius-24">
            <div className="sc-ion-label-md-s normalText4 border-radius-24">{i18next.t(`FORMS.${field.label}`)}</div>
            <IonTextarea
              mode="ios"
              id={`textarea-${field.name}`}
              slot="floating"
              onIonChange={onChange}
              value={formValue}
              autoGrow={true}
            />
          </div>
        </IonItem>
      );
    },
  },
  [INPUT_TYPE.DATE]: {
    component: (field: IEntityFormField, formik: any) => {
      const finalDate = moment(formik.values?.[field.name]).format('YYYY-MM-DDTHH:mm:ssZ');

      const getDatePicker = () => {
        if (isDesktop()) {
          return (
            <>
              {getLocalDate(finalDate)}
              <IonPopover trigger={`open-popover-${field.name}`} triggerAction="click" class="popover-date-picker">
                <IonDatetime
                  hourCycle="h23"
                  id={`datetime-${field.name}`}
                  mode="ios"
                  value={finalDate}
                  showDefaultButtons
                  cancelText={i18next.t('FORMS.CANCEL_DATE')}
                  doneText={i18next.t('FORMS.SUBMIT_DATE')}
                  locale="el-GR"
                  onIonChange={(event) => formik.setFieldValue(field.name, event.detail.value)}>
                  <span slot="time-label">{i18next.t('FORMS.TIME')}</span>
                </IonDatetime>
              </IonPopover>
            </>
          );
        }

        return (
          <>
            <IonDatetimeButton datetime={`datetime-${field.name}`} mode="ios"></IonDatetimeButton>
            <IonModal keepContentsMounted={true}>
              <IonDatetime
                hourCycle="h23"
                id={`datetime-${field.name}`}
                mode="ios"
                value={finalDate}
                showDefaultButtons
                cancelText={i18next.t('FORMS.CANCEL_DATE')}
                doneText={i18next.t('FORMS.SUBMIT_DATE')}
                locale="el-GR"
                onIonChange={(event) => formik.setFieldValue(field.name, event.detail.value)}></IonDatetime>
            </IonModal>
          </>
        );
      };

      return (
        <IonItem
          id={`open-popover-${field.name}`}
          className="width-300px border-radius-input g-select-border cursorPointer"
          mode="md">
          <div className="g-datetime-container">
            <div className="sc-ion-label-md-s normalText3 padding-b-6">{i18next.t(`FORMS.${field.label}`)}</div>
            <div className="flex-center-center-row-wrap width-100 padding-b-6">{getDatePicker()}</div>
          </div>
        </IonItem>
      );
    },
  },
};

export const getFormInput = (field: IEntityFormField, formik: any) => {
  const inputForm = mapToFormInput[field?.type as INPUT_TYPE];

  if (inputForm) {
    return inputForm.component(field, formik);
  }

  return (
    <IonItem className="width-300px border-radius-input" mode="md">
      <IonLabel position="floating">{i18next.t(`FORMS.${field.label}`)}</IonLabel>
      <IonInput
        mode="md"
        type={field.type as TextFieldTypes}
        name={field.name}
        onIonChange={formik.handleChange}
        value={formik.values?.[field.name]}
      />
    </IonItem>
  );
};

export const getOnlyViewField = (field: IEntityFormField) => {
  return (
    <IonItem className="width-300px border-radius-input" mode="md">
      <IonLabel position="floating">{i18next.t(`FORMS.${field.label}`)}</IonLabel>
      <IonInput mode="md" type={field.type as TextFieldTypes} name={field.name} value={field.value} disabled />
    </IonItem>
  );
};
